import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Firestore } from 'firebase/firestore';
import { 
  getTechnicalTemplatesByCompany,
  createTechnicalTemplate,
  updateTechnicalTemplate
} from '@cmms/api';
import { styles } from './gmp-page.style.js';
import '../../common/data-table/data-table.js';

@customElement('gmp-page')
export class GmpPage extends LitElement {
  static styles = styles;

  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;
  // When embedded inside the Technical wrapper, hide the inner page header
  @property({ type: Boolean, reflect: true }) embedded: boolean = false;

  @state() private templates: any[] = [];
  @state() private showBuilder = false;
  @state() private isSaving = false;
  @state() private activeTemplate: any = this.createEmptyTemplate();
  @state() private isLoading = false;
  @state() private error: string | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadTemplates();
  }

  updated(changed: Map<string, unknown>) {
    super.updated(changed);
    if (changed.has('companyId')) {
      this.loadTemplates();
    }
  }

  private createEmptyTemplate() {
    return {
      id: '',
      company_id: this.companyId || '',
      name: '',
      category: 'GMP',
      description: '',
      sections: [] as Array<{
        id: string;
        title: string;
        questions: Array<{
          id: string;
          text: string;
          type: 'yes_no' | 'rating' | 'text' | 'number' | 'multiple_choice';
          options?: string[];
          required: boolean;
          weight?: number;
        }>;
      }>,
      created_by: '',
      created_at: 0,
      updated_at: 0
    };
  }

  // Firestore: load templates
  private async loadTemplates() {
    try {
      this.isLoading = true;
      this.error = null;
      if (!this.firestore || !this.companyId) {
        this.templates = [];
        return;
      }
      const resp: any = await getTechnicalTemplatesByCompany(this.firestore, this.companyId, 'GMP');
      this.templates = Array.isArray(resp?.data) ? resp.data : [];
    } catch (e) {
      this.error = (e as any)?.message || 'Failed to load templates';
      this.templates = [];
    } finally {
      this.isLoading = false;
    }
  }

  // Firestore: save template (create or update)
  private async saveTemplateToDb(template: any) {
    if (!this.firestore || !this.companyId) return;
    const data = { ...template, company_id: this.companyId, category: 'GMP' };
    if (template.id) {
      await updateTechnicalTemplate(this.firestore, template.id, data);
    } else {
      await createTechnicalTemplate(this.firestore, data);
    }
  }

  private openNewTemplate = () => {
    this.activeTemplate = this.createEmptyTemplate();
    this.showBuilder = true;
    this.openDialogSoon();
  };

  private openEditTemplate = (tpl: any) => {
    this.activeTemplate = JSON.parse(JSON.stringify(tpl));
    this.showBuilder = true;
    this.openDialogSoon();
  };

  private closeBuilder = () => {
    this.showBuilder = false;
  };

  private async openDialogSoon() {
    await this.updateComplete;
    const dlg: any = this.shadowRoot?.querySelector('sl-dialog');
    if (dlg && typeof dlg.show === 'function') {
      dlg.show();
    }
  }

  private addSection = () => {
    const id = crypto.randomUUID();
    const updated = {
      ...this.activeTemplate,
      sections: [...(this.activeTemplate.sections || []), { id, title: '', questions: [] }]
    };
    this.activeTemplate = updated;
  };

  private removeSection = (idx: number) => {
    const sections = [...this.activeTemplate.sections];
    sections.splice(idx, 1);
    this.activeTemplate = { ...this.activeTemplate, sections };
  };

  private updateSectionTitle = (idx: number, title: string) => {
    const sections = [...this.activeTemplate.sections];
    sections[idx] = { ...sections[idx], title };
    this.activeTemplate = { ...this.activeTemplate, sections };
  };

  private addQuestion = (sectionIdx: number) => {
    const q = {
      id: crypto.randomUUID(),
      text: '',
      type: 'yes_no' as const,
      options: [] as string[],
      required: false,
      weight: 0
    };
    const sections = [...this.activeTemplate.sections];
    const sec = { ...sections[sectionIdx] };
    sec.questions = [...(sec.questions || []), q];
    sections[sectionIdx] = sec;
    this.activeTemplate = { ...this.activeTemplate, sections };
  };

  private removeQuestion = (sectionIdx: number, qIdx: number) => {
    const sections = [...this.activeTemplate.sections];
    const sec = { ...sections[sectionIdx] };
    const qs = [...(sec.questions || [])];
    qs.splice(qIdx, 1);
    sec.questions = qs;
    sections[sectionIdx] = sec;
    this.activeTemplate = { ...this.activeTemplate, sections };
  };

  private updateQuestion = (sectionIdx: number, qIdx: number, patch: Partial<any>) => {
    const sections = [...this.activeTemplate.sections];
    const sec = { ...sections[sectionIdx] };
    const qs = [...(sec.questions || [])];
    qs[qIdx] = { ...qs[qIdx], ...patch };
    // Ensure options array visibility control is handled in UI; keep value here
    sec.questions = qs;
    sections[sectionIdx] = sec;
    this.activeTemplate = { ...this.activeTemplate, sections };
  };

  private updateOptionsFromString(sectionIdx: number, qIdx: number, value: string) {
    const opts = value
      .split(',')
      .map(v => v.trim())
      .filter(v => v.length > 0);
    this.updateQuestion(sectionIdx, qIdx, { options: opts });
  }

  private async saveActiveTemplate() {
    try {
      this.isSaving = true;
      const tpl = this.sanitizeTemplate(this.activeTemplate);
      await this.saveTemplateToDb(tpl);
      this.showBuilder = false;
      await this.loadTemplates();
      const toast = Object.assign(document.createElement('sl-alert'), {
        variant: 'success',
        closable: true,
        innerHTML: '<sl-icon slot="icon" name="check-circle"></sl-icon><strong>Template saved.</strong>'
      });
      document.body.appendChild(toast);
      (toast as any).toast?.();
    } catch (e) {
      const toast = Object.assign(document.createElement('sl-alert'), {
        variant: 'danger',
        closable: true,
        innerHTML: `<sl-icon slot="icon" name="exclamation-octagon"></sl-icon><strong>Save failed:</strong> ${(e as any)?.message || 'Unknown error'}`
      });
      document.body.appendChild(toast);
      (toast as any).toast?.();
    } finally {
      this.isSaving = false;
    }
  }

  private sanitizeTemplate(input: any) {
    const strip = (obj: any) => JSON.parse(JSON.stringify(obj));
    const base = strip(input);
    // Remove any client-side id fields from root and nested items
    delete base.id;
    base.company_id = this.companyId;
    base.category = 'GMP';
    base.sections = (base.sections || []).map((s: any) => {
      const section = { ...s };
      delete section.id;
      section.title = section.title || '';
      section.questions = (section.questions || []).map((q: any) => {
        const qq: any = { ...q };
        delete qq.id;
        if (qq.type !== 'multiple_choice') delete qq.options;
        if (typeof qq.required !== 'boolean') qq.required = false;
        if (typeof qq.weight === 'undefined') delete qq.weight;
        return qq;
      });
      return section;
    });
    return base;
  }

  render() {
    return html`
      ${this.embedded ? html`` : html`
        <div class="page-header">
          <h2 class="page-title">GMP</h2>
          <sl-button variant="primary" size="small" @click=${this.openNewTemplate}>
            <sl-icon slot="prefix" name="plus"></sl-icon>
            New Template
          </sl-button>
        </div>
      `}
      <div class="content">
        <sl-card>
          <div class="card-header">
            <h3 class="page-title">Templates</h3>
            <sl-button variant="primary" size="small" @click=${this.openNewTemplate}>
              <sl-icon slot="prefix" name="plus"></sl-icon>
              New Template
            </sl-button>
          </div>
          ${this.isLoading ? html`
            <div class="centered"><sl-spinner></sl-spinner></div>
          ` : this.error ? html`
            <div class="error">${this.error}</div>
          ` : html`
            <data-table .config=${this.createTemplatesTableConfig()}></data-table>
          `}
        </sl-card>

        <sl-dialog label="Template Builder" ?open=${this.showBuilder} @sl-hide=${this.closeBuilder}>
          ${this.renderBuilder()}
          <div slot="footer" class="dialog-footer">
            <sl-button @click=${this.closeBuilder} ?disabled=${this.isSaving}>Cancel</sl-button>
            <sl-button variant="primary" @click=${this.saveActiveTemplate} ?loading=${this.isSaving}>
              <sl-icon slot="prefix" name="save"></sl-icon>
              Save Template
            </sl-button>
          </div>
        </sl-dialog>
      </div>
    `;
  }

  private createTemplatesTableConfig() {
    const data = Array.isArray(this.templates) ? this.templates : [];
    const formatDate = (val: any) => {
      try {
        if (!val) return '—';
        if (val?.toDate) return val.toDate().toLocaleDateString();
        if (typeof val === 'number') return new Date(val).toLocaleDateString();
        return String(val);
      } catch { return '—'; }
    };
    return {
      data,
      columns: [
        { key: 'name', label: 'Name', type: 'custom', sortable: true, renderer: (_: any, t: any) => html`<span class="primary">${t.name || 'Untitled'}</span>` },
        { key: 'description', label: 'Description', type: 'custom', renderer: (_: any, t: any) => html`<span>${t.description || ''}</span>` },
        { key: 'sections', label: 'Sections', type: 'custom', sortable: false, width: '120px', renderer: (_: any, t: any) => html`${Array.isArray(t.sections) ? t.sections.length : 0}` },
        { key: 'updated_at', label: 'Updated', type: 'custom', sortable: false, width: '140px', renderer: (_: any, t: any) => html`${formatDate(t.updated_at)}` },
        { key: 'actions', label: 'Actions', type: 'custom', width: '120px', renderer: (_: any, t: any) => html`
            <sl-button size="small" @click=${() => this.openEditTemplate(t)}><sl-icon slot="prefix" name="pencil"></sl-icon>Edit</sl-button>
          ` }
      ],
      searchPlaceholder: 'Search templates...',
      pagination: { enabled: true, dynamic: false },
      clickableRows: true,
      onRowClick: (row: any) => this.openEditTemplate(row),
      defaultSort: { key: 'updated_at', direction: 'desc' },
      emptyState: { icon: 'info-circle', message: 'No GMP templates yet. Create your first one.' }
    } as any;
  }

  private renderBuilder() {
    const t = this.activeTemplate || this.createEmptyTemplate();
    return html`
      <form class="builder"
        @submit=${(e: Event) => { e.preventDefault(); this.saveActiveTemplate(); }}
        @click=${(e: Event) => e.stopPropagation()}
        @sl-input=${(e: Event) => e.stopPropagation()}
        @sl-change=${(e: Event) => e.stopPropagation()}
        @sl-open-change=${(e: Event) => e.stopPropagation()}
        @sl-show=${(e: Event) => e.stopPropagation()}
        @sl-hide=${(e: Event) => e.stopPropagation()}
      >
        <sl-input label="Template Name" placeholder="Enter name" .value=${t.name}
          @sl-input=${(e: any) => this.activeTemplate = { ...t, name: e.target.value }}></sl-input>
        <sl-textarea label="Description" placeholder="Describe this template" .value=${t.description}
          @sl-input=${(e: any) => this.activeTemplate = { ...t, description: e.target.value }}></sl-textarea>

        <div class="sections">
          ${(t.sections || []).map((s: any, i: number) => html`
            <sl-details summary=${`Section ${i + 1}: ${s.title || '(untitled)'}` } open>
              <div class="section-body">
                <div class="section-header">
                  <strong>Section ${i + 1}</strong>
                  <sl-button size="small" variant="danger" @click=${() => this.removeSection(i)}>
                    <sl-icon slot="prefix" name="trash"></sl-icon>
                    Remove Section
                  </sl-button>
                </div>

                <sl-input label="Section Title" placeholder="Title" .value=${s.title}
                  @sl-input=${(e: any) => this.updateSectionTitle(i, e.target.value)}></sl-input>

                <div class="questions">
                  ${(s.questions || []).map((q: any, qi: number) => html`
                    <div>
                      <div class="question-card">
                        <div class="question-header">
                          <strong>Question ${qi + 1}</strong>
                          <sl-button size="small" variant="danger" @click=${() => this.removeQuestion(i, qi)}>
                            <sl-icon slot="prefix" name="trash"></sl-icon>
                            Remove
                          </sl-button>
                        </div>
                        <sl-input label="Question" placeholder="Enter question" .value=${q.text}
                          @sl-input=${(e: any) => this.updateQuestion(i, qi, { text: e.target.value })}></sl-input>
                        <div class="question-grid">
                          <sl-select label="Type" .value=${q.type}
                            @sl-change=${(e: any) => this.updateQuestion(i, qi, { type: e.target.value })}>
                            <sl-option value="yes_no">Yes/No</sl-option>
                            <sl-option value="rating">Rating</sl-option>
                            <sl-option value="text">Text</sl-option>
                            <sl-option value="number">Number</sl-option>
                            <sl-option value="multiple_choice">Multiple Choice</sl-option>
                          </sl-select>
                          <sl-switch ?checked=${!!q.required} @sl-change=${(e: any) => this.updateQuestion(i, qi, { required: e.target.checked })}>Required</sl-switch>
                          <sl-input label="Weight" type="number" .value=${String(q.weight ?? 0)}
                            @sl-input=${(e: any) => this.updateQuestion(i, qi, { weight: Number(e.target.value || 0) })}></sl-input>
                        </div>
                        ${q.type === 'multiple_choice' ? html`
                          <sl-input label="Options (comma-separated)" .value=${(q.options || []).join(', ')}
                            @sl-input=${(e: any) => this.updateOptionsFromString(i, qi, e.target.value)}></sl-input>
                        ` : ''}
                      </div>
                      ${qi < ((s.questions?.length || 0) - 1) ? html`<sl-divider></sl-divider>` : ''}
                    </div>
                  `)}

                  <sl-button size="small" @click=${() => this.addQuestion(i)}>
                    <sl-icon slot="prefix" name="plus"></sl-icon>
                    Add Question
                  </sl-button>
                </div>
              </div>
            </sl-details>
          `)}
        </div>

        <div>
          <sl-button @click=${this.addSection}>
            <sl-icon slot="prefix" name="plus"></sl-icon>
            Add Section
          </sl-button>
        </div>
      </form>
    `;
  }
}

export default GmpPage;

declare global {
  interface HTMLElementTagNameMap {
    'gmp-page': GmpPage;
  }
}
